import { IntlProvider } from "react-intl";
import contactMessages from "../translate/contactMessages";
import { useContext } from "react";
import "bootstrap/dist/css/bootstrap.css";
import { Row, Col } from "react-bootstrap";
import Logo from "../images/contact.png";
import Me from "../images/kontakt.jpg";
import { LangContext } from "../App";
import { Helmet } from "react-helmet-async";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import AlternateEmailIcon from "@mui/icons-material/AlternateEmail";
import LanguageIcon from "@mui/icons-material/Language";
import BusinessIcon from "@mui/icons-material/Business";
import { motion, AnimatePresence } from "framer-motion";
import { textVariants } from "../components/textVariants";

const Contact = () => {
  const language = useContext(LangContext);
  const styles = {
    container: {
      maxWidth: "1140px",
      margin: "0 auto",
      padding: "80px 24px",
      fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
    },
    cardLeft: {
      background: "#ffffff",
      borderRadius: "24px",
      padding: "40px",
      boxShadow: "0 10px 30px -10px rgba(0,0,0,0.06)",
      border: "1px solid #f3f4f6",
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      height: "100%",
      width: "100%",
    },
    avatarContainer: {
      display: "flex",
      alignItems: "center",
      gap: "20px",
      marginBottom: "32px",
      flexWrap: "wrap",
    },
    avatarImg: {
      width: "160px",
      height: "160px",
      borderRadius: "20%",
      objectFit: "cover",
      border: "3px solid #eef2ff",
      boxShadow: "0 4px 12px rgba(79, 70, 229, 0.1)",
    },
    cardRight: {
      background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
      borderRadius: "24px",
      padding: "40px",
      color: "#f8fafc",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      height: "100%",
      width: "100%",
      boxShadow: "0 20px 40px -15px rgba(15,23,42,0.3)",
    },
    name: {
      fontSize: "24px",
      fontWeight: "700",
      fontFamily: "Verdana",
      letterSpacing: "0.15em",
      color: "#334155",
      textTransform: "uppercase",
      margin: 0,
    },
    infoLink: {
      display: "flex",
      alignItems: "center",
      gap: "14px",
      color: "#334155",
      textDecoration: "none",
      fontSize: "16px",
      fontWeight: "500",
      marginBottom: "20px",
      transition: "color 0.2s ease",
    },
    iconWrapper: {
      color: "#4f46e5",
      backgroundColor: "#eef2ff",
      padding: "8px",
      borderRadius: "10px",
      display: "inline-flex",
    },
    divider: {
      border: "0",
      height: "1px",
      backgroundColor: "#f1f5f9",
      margin: "32px 0",
    },
    footerRow: {
      display: "flex",
      alignItems: "center",
      gap: "20px",
    },
    logoImg: {
      height: "45px",
      objectFit: "contain",
    },
    addressText: {
      margin: 0,
      fontSize: "14px",
      color: "#475569",
      fontWeight: "500",
    },
    nipText: {
      margin: "4px 0 0 0",
      fontSize: "12px",
      color: "#475569",
      letterSpacing: "0.05em",
    },
    rightTitle: {
      fontSize: "26px",
      fontWeight: "800",
      lineHeight: "1.3",
      marginBottom: "20px",
      color: "#ffffff",
    },
    rightText: {
      fontSize: "16px",
      lineHeight: "1.7",
      color: "#cbd5e1",
      marginBottom: "24px",
    },
    rightBadge: {
      display: "inline-block",
      fontSize: "13px",
      fontWeight: "600",
      color: "#38bdf8",
      backgroundColor: "rgba(56,189,248,0.1)",
      padding: "6px 14px",
      borderRadius: "99px",
      alignSelf: "flex-start",
    },
  };

  return (
    <>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ContactPage",
            name: "Kontakt - Fullstack Developer",
            url: "https://michaljaskulski.pl/contact",
            mainEntity: {
              "@type": "Person",
              name: "Michał Jaskulski",
              jobTitle: "Fullstack Developer",
              telephone: "+48602209858",
              email: "kontakt@michaljaskulski.pl",
              url: "https://michaljaskulski.pl",
            },
          })}
        </script>
        <title>Mikołów | Katowice | Śląskie</title>
        <meta name="description" content="Mikołów, Katowice, Śląskie" />
      </Helmet>

      <div className="bodyBackground" style={{ backgroundColor: "#f8fafc" }}>
        <IntlProvider locale={language} messages={contactMessages[language]}>
          <AnimatePresence>
            <motion.div
              style={styles.container}
              initial="hidden"
              animate="visible"
              exit="hidden"
              variants={textVariants}
            >
              <div style={styles.container}>
                <Row className="g-4">
                  <Col xs={12} lg={5} className="d-flex">
                    <div style={styles.cardLeft}>
                      <div>
                        <div style={styles.avatarContainer}>
                          {Me && (
                            <img
                              src={Me}
                              alt="Michał Jaskulski"
                              style={styles.avatarImg}
                            />
                          )}
                          <p style={styles.name}>Michał Jaskulski</p>
                        </div>

                        <div style={{ marginTop: "20px" }}>
                          <a href="tel:+48602209858" style={styles.infoLink}>
                            <span style={styles.iconWrapper}>
                              <PhoneAndroidIcon fontSize="small" />
                            </span>
                            +48 602 209 858
                          </a>

                          <a
                            href="mailto:michal.jaskulski@allworks.pl"
                            style={styles.infoLink}
                          >
                            <span style={styles.iconWrapper}>
                              <AlternateEmailIcon fontSize="small" />
                            </span>
                            michal.jaskulski@allworks.pl
                          </a>

                          <a
                            href="https://michaljaskulski.pl"
                            target="_blank"
                            rel="noreferrer"
                            style={styles.infoLink}
                          >
                            <span style={styles.iconWrapper}>
                              <LanguageIcon fontSize="small" />
                            </span>
                            michaljaskulski.pl
                          </a>
                        </div>
                      </div>

                      <div>
                        <hr style={styles.divider} />
                        <div style={styles.footerRow}>
                          {Logo && (
                            <div>
                              <img
                                style={styles.logoImg}
                                src={Logo}
                                alt="Logo"
                              />
                            </div>
                          )}
                          <div>
                            <p style={styles.addressText}>
                              <BusinessIcon
                                fontSize="small"
                                style={{
                                  color: "#94a3b8",
                                  marginRight: "4px",
                                  verticalAlign: "text-bottom",
                                }}
                              />
                              43-190 Mikołów <br /> ul. Brzozowa 14D
                            </p>
                            <p style={styles.nipText}>NIP: 635 184 83 80</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Col>

                  <Col xs={12} lg={7} className="d-flex">
                    <div style={styles.cardRight}>
                      {language === "pl" && (
                        <>
                          <h2 style={styles.rightTitle}>
                            Masz pomysł na aplikację lub chcesz rozwinąć
                            istniejący system?
                          </h2>
                          <p style={styles.rightText}>
                            Opisz krótko swoje potrzeby — pomogę dobrać
                            rozwiązanie, zaproponuję kierunek techniczny i
                            przedstawię możliwy zakres prac. Współpracuję z
                            firmami, które traktują technologię jako inwestycję
                            w rozwój.
                          </p>
                          <span style={styles.rightBadge}>
                            Odpowiadam zwykle w ciągu 24 godzin.
                          </span>
                        </>
                      )}
                      {language === "en" && (
                        <>
                          <h2 style={styles.rightTitle}>
                            Have an idea for a web application or looking to
                            improve an existing system?
                          </h2>
                          <p style={styles.rightText}>
                            Tell me about your project and requirements. I'll
                            help you identify the best solution, recommend the
                            right technical approach, and define the scope of
                            work. I partner with businesses that view technology
                            as a strategic investment in growth.
                          </p>
                          <span style={styles.rightBadge}>
                            I typically respond within one business day.
                          </span>
                        </>
                      )}
                    </div>
                  </Col>
                </Row>
              </div>
            </motion.div>
          </AnimatePresence>
        </IntlProvider>
      </div>
    </>
  );
};

export default Contact;
